// src/components/admin/employees/EmployeeAnalytics.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
    Users, TrendingUp, TrendingDown, Loader2, RefreshCw, BarChart3, Calendar
} from 'lucide-react';
import {
    BarChart, Bar, PieChart, Pie, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell
} from 'recharts';
import analyticsService from '../../../services/analytics.service';

const CHART_COLORS = [
    '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#3B82F6',
    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6B7280'
];

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => {
    const { isDarkMode } = useTheme();
    const colorMap = {
        green: 'text-green-500', blue: 'text-blue-500', red: 'text-red-500',
        yellow: 'text-yellow-500', purple: 'text-purple-500', indigo: 'text-indigo-500'
    };
    return (
        <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{title}</span>
                <Icon className={`w-5 h-5 ${colorMap[color] || 'text-gray-500'}`} />
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
            {subtitle && <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>}
        </div>
    );
};

const EmployeeAnalytics = () => {
    const { isDarkMode } = useTheme();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());

    const [headcountData, setHeadcountData] = useState(null);
    const [demographics, setDemographics] = useState(null);
    const [attritionData, setAttritionData] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [hcRes, demoRes, attrRes] = await Promise.allSettled([
                analyticsService.getHeadcountTrends(12),
                analyticsService.getEmployeeDemographics(),
                analyticsService.getAttritionAnalytics(selectedYear)
            ]);

            if (hcRes.status === 'fulfilled') setHeadcountData(hcRes.value?.data || hcRes.value);
            if (demoRes.status === 'fulfilled') setDemographics(demoRes.value?.data || demoRes.value);
            if (attrRes.status === 'fulfilled') setAttritionData(attrRes.value?.data || attrRes.value);
        } catch (err) {
            console.error('Error loading employee analytics:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedYear]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const yearOptions = [];
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 3; y--) yearOptions.push(y);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading analytics...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
                <p>Failed to load analytics: {error}</p>
                <button onClick={loadData} className="mt-2 text-sm underline">Retry</button>
            </div>
        );
    }

    const hcTrends = headcountData?.trends || [];
    const currentHeadcount = headcountData?.currentHeadcount || 0;
    const ageGroups = demographics?.age || [];
    const genderDist = demographics?.gender || [];
    const tenureDist = demographics?.tenure || [];
    const empTypes = demographics?.employmentType || [];
    const attrRate = attritionData?.attritionRate || 0;
    const attrMonthly = attritionData?.monthlyTrends || [];
    const attrByDept = attritionData?.byDepartment || [];

    return (
        <div className="space-y-6 p-4">
            {/* Filters */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-3">
                    <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className={`px-3 py-2 rounded-lg border text-sm ${
                            isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    >
                        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <button
                    onClick={loadData}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard title="Current Headcount" value={currentHeadcount} icon={Users} color="blue" />
                <StatCard
                    title="Attrition Rate"
                    value={attrRate ? `${Number(attrRate).toFixed(1)}%` : '-'}
                    icon={TrendingDown} color="red"
                    subtitle={`Year ${selectedYear}`}
                />
                <StatCard
                    title="New Hires"
                    value={hcTrends.reduce((s, t) => s + (t.hired || 0), 0)}
                    icon={TrendingUp} color="green"
                    subtitle="Last 12 months"
                />
                <StatCard
                    title="Terminations"
                    value={attritionData?.totalTerminations || hcTrends.reduce((s, t) => s + (t.terminated || 0), 0)}
                    icon={TrendingDown} color="yellow"
                    subtitle={`Year ${selectedYear}`}
                />
            </div>

            {/* Headcount Trends */}
            {hcTrends.length > 0 && (
                <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Headcount Trends (Last 12 Months)
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={hcTrends.map(t => ({
                            name: t.month || '',
                            hired: t.hired || 0,
                            terminated: t.terminated || 0
                        }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                            <XAxis dataKey="name" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} tick={{ fontSize: 11 }} />
                            <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                            <Tooltip contentStyle={{
                                backgroundColor: isDarkMode ? '#1F2937' : '#FFF',
                                border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                                color: isDarkMode ? '#F9FAFB' : '#111827'
                            }} />
                            <Legend />
                            <Bar dataKey="hired" name="Hired" fill="#10B981" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="terminated" name="Terminated" fill="#EF4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Demographics — 2x2 grid */}
            <div>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Employee Demographics
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Gender Distribution */}
                    <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gender</h4>
                        {genderDist.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie data={genderDist.map(g => ({ name: g.type || g.gender || 'Unknown', value: g.count || 0 }))}
                                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                                        label={(e) => `${e.name}: ${e.value}`}
                                    >
                                        {genderDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data</p>}
                    </div>

                    {/* Age Distribution */}
                    <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Age Groups</h4>
                        {ageGroups.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={ageGroups.map(a => ({ name: a.group || 'Unknown', count: a.count || 0 }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                                    <XAxis dataKey="name" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} tick={{ fontSize: 11 }} />
                                    <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                                    <Tooltip />
                                    <Bar dataKey="count" name="Employees" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data</p>}
                    </div>

                    {/* Tenure Distribution */}
                    <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Tenure</h4>
                        {tenureDist.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={tenureDist.map(t => ({ name: t.group || 'Unknown', count: t.count || 0 }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                                    <XAxis dataKey="name" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} tick={{ fontSize: 11 }} />
                                    <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                                    <Tooltip />
                                    <Bar dataKey="count" name="Employees" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data</p>}
                    </div>

                    {/* Employment Type */}
                    <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Employment Type</h4>
                        {empTypes.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie data={empTypes.map(e => ({ name: e.type || 'Unknown', value: e.count || 0 }))}
                                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                                        label={(e) => `${e.name}: ${e.value}`}
                                    >
                                        {empTypes.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data</p>}
                    </div>
                </div>
            </div>

            {/* Attrition by Department */}
            {attrByDept.length > 0 && (
                <div className={`rounded-lg shadow p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Attrition by Department ({selectedYear})
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={attrByDept.map(d => ({ name: d.department || 'Unknown', count: d.count || 0 }))}
                            margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                            <XAxis dataKey="name" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                                angle={-30} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
                            <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                            <Tooltip />
                            <Bar dataKey="count" name="Terminations" fill="#EF4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default EmployeeAnalytics;
